import * as THREE from 'three';
import { getAdTexture } from './textures';
import { getLedSettings, type LedSettings } from './LedSettings';
const transitions = ['cut', 'fade', 'wipe', 'slide'];

export function createLedAdvertising(readSettings: () => Readonly<LedSettings> = getLedSettings): { material: THREE.MeshBasicMaterial; update: (elapsed: number) => void } {
  const time = { value: 0 };
  const color = { value: new THREE.Color() };
  const timing = { value: new THREE.Vector4() };
  const flags = { value: new THREE.Vector4() };
  const transition = { value: 2 };
  const material = new THREE.MeshBasicMaterial({ map: getAdTexture(), toneMapped: false });
  material.name = 'court-led-panels';
  material.onBeforeCompile = shader => {
    shader.uniforms.ledTime = time;
    shader.uniforms.ledColor = color;
    shader.uniforms.ledTiming = timing;
    shader.uniforms.ledFlags = flags;
    shader.uniforms.ledTransition = transition;
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float ledTime;\nuniform vec3 ledColor;\nuniform vec4 ledTiming;\nuniform vec4 ledFlags;\nuniform float ledTransition;')
      .replace('#include <map_fragment>', /* glsl */`
        float panelIndex = floor(min(vMapUv.x, 0.999999) * 7.0);
        vec2 panelUv = vec2(fract(vMapUv.x * 7.0), vMapUv.y);
        float panelTime = ledTime + panelIndex * ledTiming.x * (0.37 / 6.0);
        float age = mix(ledTiming.x, mod(panelTime, ledTiming.x), ledFlags.x);
        float sourceIndex = mod(panelIndex + floor(panelTime / ledTiming.x) * ledFlags.x, 7.0);
        float progress = clamp(age / ledTiming.y, 0.0, 1.0);

        vec2 grid = vec2(224.0, 48.0);
        vec2 cell = fract(panelUv * grid) - 0.5;
        float footprint = max(fwidth(panelUv.x) * grid.x, fwidth(panelUv.y) * grid.y);
        // Blend distant subpixel LEDs into their average to avoid shimmering.
        float detail = (1.0 - smoothstep(0.75, 1.6, footprint)) * ledFlags.w;
        vec2 pixelUv = mix(panelUv, (floor(panelUv * grid) + 0.5) / grid, detail);
        vec3 content = texture2D(map, vec2((sourceIndex + pixelUv.x) / 7.0, pixelUv.y)).rgb;
        if (progress < 1.0 && ledTransition > 0.5) {
          float previousIndex = mod(sourceIndex + 6.0, 7.0);
          if (ledTransition > 2.5) {
            float slidingX = pixelUv.x + progress;
            float index = mix(previousIndex, sourceIndex, step(1.0, slidingX));
            content = texture2D(map, vec2((index + fract(slidingX)) / 7.0, pixelUv.y)).rgb;
          } else {
            vec3 previous = texture2D(map, vec2((previousIndex + pixelUv.x) / 7.0, pixelUv.y)).rgb;
            float blend = ledTransition < 1.5 ? progress : smoothstep(panelUv.x - 0.06, panelUv.x + 0.06, progress * 1.12 - 0.06);
            content = mix(previous, content, blend);
          }
        }
        float peak = max(content.r, max(content.g, content.b));
        content = mix(content, ledColor * peak, 0.25 * ledFlags.z);

        float dots = mix(mix(1.0, 0.66, ledFlags.w), 1.0 - smoothstep(0.32, 0.45, length(cell)), detail);
        float sweep = (1.0 - smoothstep(0.0, 0.10, abs(panelUv.x - fract(ledTime * 0.14 + panelIndex * 0.13)))) * ledFlags.y;
        float blink = 1.0 - ledTiming.z * (1.0 - step(0.5, fract(age * ledTiming.w))) * (1.0 - step(ledTiming.y, age));
        float edge = min(min(panelUv.x, 1.0 - panelUv.x) * 4.57, min(panelUv.y, 1.0 - panelUv.y));
        float frame = 1.0 - smoothstep(0.018, 0.035, edge);
        vec3 display = content * (1.0 + sweep * 0.28) * blink;
        display = mix(display, ledColor * (0.55 + sweep * 0.45), frame);
        diffuseColor.rgb *= mix(vec3(0.002, 0.004, 0.006), display, dots);
      `);
  };
  material.customProgramCacheKey = () => 'court-led-v2';
  return {
    material,
    update: elapsed => {
      const settings = readSettings();
      const period = settings.interval * 7;
      time.value = ((elapsed % period) + period) % period;
      color.value.setHSL(settings.colorCycle ? time.value / period : 0.12, 0.85, 0.6);
      timing.value.set(settings.interval, settings.duration, settings.blink / 100, settings.blinkRate);
      flags.value.set(Number(settings.rotate), Number(settings.sweep), Number(settings.colorCycle), Number(settings.pixels));
      transition.value = transitions.indexOf(settings.transition);
    },
  };
}
